import React from 'react';
import { fabric } from 'sunzi-fabric';
import { Loading } from '@/components';
import { PasterOption, LineTextOption, PastersConnect, RuleLineText, ValidatorStatus } from '@/sunzi';
import { MugType, MugRules, MugBottomImage } from './mug';
import { i18n, bottom7nCrop, loadFont, loadImageFromURL, loadHTMLImage, delay } from '@/utils';
import { fabricConfig } from '@/config';
import styles from './style.less';

const { mirror, scale, edit, rotate } = fabricConfig.control;
const { borderColor, borderScaleFactor, cornerSize, textPadding } = fabricConfig.attribute;

interface ContentProps {
  className: string;
  pasters: PasterOption[];
  lineTexts: LineTextOption[];
  connects?: PastersConnect[];
  bottomImage?: MugBottomImage;
  watermarkURL?: string;
  rules: MugRules;
  type?: MugType;
  onSynchroObjects: () => void;
  onSynchroActiveObject: (target?: fabric.Object) => void;
  onPasterPress: (paster: PasterOption) => void;
  onTextPress?: (text: LineTextOption) => void;
}

interface ContentState {
  initLoading: boolean;
  loading: boolean;
  error: boolean;
}

class Content extends React.Component<ContentProps, ContentState > {
  static Validator: (rule: RuleLineText, lineTexs: LineTextOption[]) => {
    status: ValidatorStatus,
    message: React.ReactNode[]
  };

  private _wrapper: HTMLDivElement | null | undefined;
  private _element: HTMLCanvasElement | null | undefined;
  public _canvas: fabric.Canvas | undefined;
  private _multiple: number = 1.0;

  constructor(props: ContentProps) {
    super(props);
    // set multiple
    this._multiple = Math.min(
      props.rules.bottomImage.width / 375,
      props.rules.bottomImage.height / 375
    );
    this.state = {
      initLoading: true,
      loading: false,
      error: false,
    }
  }

  async componentDidMount() {
    if (this._wrapper && this._element) {
      const { offsetWidth } = this._wrapper;
      let offsetHeight = document.body.clientHeight - 182;
      if (window.innerWidth > 768)
        offsetHeight = 600 - 182;
      const { rules, pasters, lineTexts } = this.props;
      this._canvas = new fabric.Canvas(this._element, {
        selection: false,
      });
      // 根据底图 + 底图规则设置底图
      if (rules.bottomImage) {
        const scale = Math.min(
          (offsetWidth - 20) / rules.bottomImage.width,
          (offsetHeight - 20) / rules.bottomImage.height
        );
        // set css and backstore dimensions
        this._canvas.setDimensions({
          width: rules.bottomImage.width * scale,
          height: rules.bottomImage.height * scale,
        });
        // set canvas backstore dimensions
        this._canvas.setDimensions({
          width: rules.bottomImage.width,
          height: rules.bottomImage.height
        }, { backstoreOnly: true });
      }
      // 设置默认控制器图标
      const [ tl, tr, bl, br ] = await Promise.all([
        loadHTMLImage(mirror),
        loadHTMLImage(scale),
        loadHTMLImage(edit),
        loadHTMLImage(rotate),
      ]);
      this._canvas.setControlImage('tl', tl);
      this._canvas.setControlImage('tr', tr);
      this._canvas.setControlImage('bl', bl);
      this._canvas.setControlImage('br', br);
      // 渲染贴纸
      await Promise.all(pasters.map(item => this.renderPaster(item)));
      // 增加渲染层级调整
      pasters.forEach(({ index, _group }) => {
        _group && _group.moveTo(index);
      });
      // 渲染刻字
      await Promise.all(lineTexts.map(item => this.renderLineText(item)));
      // 渲染完成
      this.setState({
        initLoading: false
      });
      this.props.onSynchroObjects();
      // 默认激活
      const _active = this.getObjects()[0];
      this._canvas.setActiveObject(_active);
      this.props.onSynchroActiveObject(_active);
      // 渲染
      this._canvas.renderAll();
      // 根据刻字规则 限定刻字的移动范围
      this._canvas.on({
        'object:moving': (e: fabric.IEvent) => {
          const target: any = e.target;
          const { left = 0, top = 0, width = 0, height = 0, originY, scaleX, scaleY } = target;
          // 直线刻字区域限制
          if (rules.lineText && target.type === 'text') {
            // 计算限制区域
            const { limitTop, limitLeft, limitWidth, limitHeight } = rules.lineText;
            if (limitTop && limitLeft && limitWidth && limitHeight) {
              target.left = Math.min(
                Math.max(left, limitLeft),
                limitLeft + limitWidth - width * scaleX
              );
              target.top = Math.min(
                Math.max(top, limitTop),
                limitTop + limitHeight - height * scaleY
              );
            }
          }
          // 贴纸区域限制
          if (this._canvas && target.type === 'group') {
            target.left = Math.min(
              Math.max(left, 0),
              this._canvas.getWidth() - width * scaleX
            );
            if (originY === 'bottom')
              target.top = Math.min(
                Math.max(top, height * scaleY),
                this._canvas.getHeight()
              );
            else
              target.top = Math.min(
                Math.max(top, 0),
                this._canvas.getHeight() - height * scaleY
              );
          }
        },
        'mouse:down': (e: fabric.IEvent) => {
          this.props.onSynchroActiveObject(e.target);
        }
      });
    }
  }

  componentWillUnmount() {
    this._canvas?.clear();
    this._canvas?.setDimensions({
      width: 0,
      height: 0
    });
  }

  refWrapper = (node: HTMLDivElement) => {
    this._wrapper = node;
  }

  refElement = (node: HTMLCanvasElement) => {
    this._element = node;
  }

  /**
   * 贴纸渲染
   */
  public renderPaster = async (paster: PasterOption) => {
    this.setState({
      loading: true
    });
    const { connects, rules, onPasterPress, onSynchroObjects } = this.props;
    let _group = paster._group;
    // 重新计算尺寸
    const {
      width,
      height,
      paddingLeft,
      paddingRight,
      paddingTop,
      paddingBottom
    } = this._getPasterMaxSize(paster, connects);
    // 如果是初次渲染
    if (_group === undefined) {
      _group = new fabric.Group();
      // 如果贴纸以底部为基准
      _group.set({
        left: paster.left,
        top: paster.top,
        width,
        height,
        angle: paster.angle,
        scaleX: paster.scale,
        scaleY: paster.scale,
        borderColor,
        borderScaleFactor: borderScaleFactor * this._multiple,
        cornerSize: cornerSize * this._multiple
      });
      // 设置贴纸规则
      if (rules.paster) {
        _group.set({
          lockMovementX: rules.paster.lockMovementX,
          lockMovementY: rules.paster.lockMovementY,
          lockRotation: rules.paster.lockRotation,
        });
        // 锁定旋转
        if (rules.paster.lockRotation)
          _group.setControlVisible('br', false);
        // 锁定编辑
        if (rules.paster.lockEdit)
          _group.setControlVisible('bl', false);
        // 锁定镜像
        if (rules.paster.lockFlipX)
          _group.setControlVisible('tl', false);
        // 锁定缩放
        if (rules.paster.lockScale)
          _group.setControlVisible('br', false);
        // 如果贴纸以底部为基准
        if (rules.paster.originBottom) {
          _group.set({
            top: paster.top + height * paster.scale,
            originY: 'bottom'
          })
        }
      }
    } else {
      // 更新操作
      _group.set({
        width,
        height,
      });
      const { left = 0, top = 0, originY, scaleX = 0, scaleY = 0 } = _group;
      if (this._canvas) {
        _group.left = Math.min(
          Math.max(left, 0),
          this._canvas.getWidth() - width * scaleX
        );
        if (originY === 'bottom')
          _group.top = Math.min(
            Math.max(top, height * scaleY),
            this._canvas.getHeight()
          );
        else
          _group.top = Math.min(
            Math.max(top, 0),
            this._canvas.getHeight() - height * scaleY
          );
      }
      // 清空主体和配件
      _group.remove(..._group.getObjects());
    }
    // 编辑监听
    _group.set({
      onTLPress: (target: fabric.Object) => {
        target.flipX = !target.flipX;
        this._canvas?.renderAll();
      },
      onBLPress: () => {
        onPasterPress && onPasterPress(paster);
      },
    })
    // 并行获取所有图片
    const [ _image, _cropImage, ...annexs ] = await Promise.all([
      loadImageFromURL(paster.image),
      loadImageFromURL(paster.cropImage),
      ...paster.annexs.map(item => loadImageFromURL(item.image))
    ]);

    // 主体实际容纳的宽和高
    const _innerWidth = width - paddingLeft - paddingRight;
    const _innerHeight = height - paddingTop - paddingBottom;

    const _scale = Math.min(
      _innerWidth / (_image.width || _innerWidth),
      _innerHeight / (_image.height || _innerHeight)
    );

    // 主体和crop图的option
    const _imageOption = {
      scaleX: _scale,
      scaleY: _scale,
      left: paddingLeft - width / 2,
      top: paddingTop - height / 2
    };

    // 渲染主体
    _image.set(_imageOption);
    _group.add(_image);

    // 配件渲染
    paster.annexs.forEach((annex, index) => {
      const _annex = annexs[index];
      if (annex._connect) {
        _annex.set({
          scaleX: annex._connect.scale,
          scaleY: annex._connect.scale,
          left: (paddingLeft - annex._connect.paddingLeft + annex._connect.left) - width / 2,
          top: (paddingTop - annex._connect.paddingTop + annex._connect.top) - height / 2,
          angle: annex._connect.angle,
        });
        if (_group)
          _group.add(_annex);
        _annex.moveTo(annex._connect.level);
      }
    });

    _cropImage.set(_imageOption);
    _group.add(_cropImage);
    paster._group = _group;
    // 渲染到画布
    if (this._canvas) {
      if (!this._canvas.contains(_group))
        this._canvas.add(_group);
      // 设置渲染层级
      this._canvas.renderAll();
      paster._group = _group;
      this.setState({
        loading: false
      });
      onSynchroObjects();
      this.discardActiveObject();
    }
  }

  /**
   * 刻字渲染
   */
  public renderLineText = async (lineText: LineTextOption) => {
    this.setState({
      loading: true
    });
    const { rules, onTextPress, onSynchroObjects } = this.props;
    let { _text: _lineText, value, fontColor = '#000', fontSize, font } = lineText;
    // 加载字体 有缓存机制
    try {
      await loadFont(lineText.font.name);
    } catch(e) {
      this.setState({
        error: true
      }, async () => {
        // TODO
        await delay(3000);
        this.setState({
          error: false
        });
      });
    }

    if (_lineText === undefined) {
      _lineText = new fabric.Text(value, {
        left: lineText.left,
        top: lineText.top,
        fontSize,
        fontFamily: font.name,
        fill: fontColor,
        borderColor,
        padding: textPadding * this._multiple,
        borderScaleFactor: borderScaleFactor * this._multiple,
        cornerSize: cornerSize * this._multiple
      });

      _lineText.setControlsVisibility({
        tl: false
      });

      // 设置刻字规则
      if (rules.lineText) {
        _lineText.set({
          lockMovementX: rules.lineText.lockMovementX,
          lockMovementY: rules.lineText.lockMovementY,
          lockRotation: rules.lineText.lockRotation,
        });
        // 如果是锁定旋转 不显示控制器
        if (rules.lineText.lockScale)
          _lineText.setControlVisible('tr', false);
        if (rules.lineText.lockRotation)
          _lineText.setControlVisible('br', false);
        // 如果锁定编辑
        if (rules.lineText.lockEdit)
          _lineText.setControlVisible('bl', false);
      }
    } else {
      _lineText.setOptions({
        text: value,
        fontSize,
        fontFamily: font.name,
        fill: fontColor,
      });
    }
    // 编辑监听
    _lineText.set({
      onBLPress: () => {
        onTextPress && onTextPress(lineText);
      },
    });

    if (this._canvas) {
      if (!this._canvas.contains(_lineText))
        this._canvas.add(_lineText);
      this._canvas.renderAll();
      lineText._text = _lineText;
      this.setState({
        loading: false
      });
      onSynchroObjects();
      this.discardActiveObject();
    }
  }

  /**
   * 根据配件需要的内边距计算贴纸的尺寸
   */
  private _getPasterMaxSize = (paster: PasterOption, connects?: PastersConnect[]) : {
    width: number,
    height: number,
    paddingLeft: number,
    paddingRight: number,
    paddingTop: number,
    paddingBottom: number
  } => {
    let width = 0, // paster.width,
      height = 0,
      paddingLeft = 0,
      paddingRight = 0,
      paddingTop = 0,
      paddingBottom = 0;

    if (connects) {
      paster.annexs.forEach(annex => {
        // 查询到主体和配件类的关联数据
        annex._connect = connects.find(item => item.pid === paster.id && item.acid === annex.facid);
        if (annex._connect) {
          width = Math.max(annex._connect.width, width);
          height = Math.max(annex._connect.height, height);
          paddingLeft = Math.max(annex._connect.paddingLeft, paddingLeft);
          paddingRight = Math.max(annex._connect.paddingRight, paddingRight);
          paddingTop = Math.max(annex._connect.paddingTop, paddingTop);
          paddingBottom = Math.max(annex._connect.paddingBottom, paddingBottom);
        }
      });
    }
    // 没有关联尺寸的话 走贴纸的默认尺寸
    if (width === 0) width = paster.width;
    if (height === 0) height = paster.height;
    return {
      width,
      height,
      paddingLeft,
      paddingRight,
      paddingTop,
      paddingBottom
    };
  }

  // 向下一级
  public sendBackwards = () => {
    const active = this._canvas && this._canvas.getActiveObject();
    if (active)
      active.sendBackwards();
  }

  // 向上一级
  public bringForward = () => {
    const active = this._canvas && this._canvas.getActiveObject();
    if (active)
      active.bringForward();
  }

  // 最底部
  public sendToBack = () => {
    const active = this._canvas && this._canvas.getActiveObject();
    if (active)
      active.sendToBack();
  }

  // 最顶部
  public bringToFront = () => {
    const active = this._canvas && this._canvas.getActiveObject();
    if (active)
      active.bringToFront();
  }

  // 获取所有子元素
  public getObjects = () => {
    if (this._canvas) {
      const objects = this._canvas.getObjects();
      // group在前，text在后
      return objects.filter((target: fabric.Object) => target.type === 'group')
        .concat(objects.filter((target: fabric.Object) => target.type === 'text'));
    }
    return [];
  }

  // 设置激活元素
  public setActiveObject = (target: fabric.Object) => {
    if (this._canvas) {
      this._canvas.setActiveObject(target);
      this._canvas.renderAll();
    }
  }

  /** 取消激活选中 */
  public discardActiveObject = () => {
    if (this._canvas) {
      this._canvas.discardActiveObject();
      this._canvas.renderAll();
    }
  }

  /** 设置加载 */
  startLoading = () => {
    this.setState({
      loading: true
    });
  }

  /** 底图加载 */
  onBottomImageLoad = () => {
    this.setState({
      loading: false
    });
  }

  /** 生成带位置信息的image */
  public toImage = async (context?: CanvasRenderingContext2D | null): Promise<HTMLImageElement | undefined> => {
    const { rules, lineTexts } = this.props;
    if (this._canvas && rules.bottomImage) {
      // 清除所有模块下的非必填文字默认值
      const uninput: fabric.Text[] = [];
      // 刻字不是必填 & 如果存在刻字 & 没有输入内容 => 清空
      if (!rules.lineText?.isRequire) {
        lineTexts.forEach(item => {
          if (item._text && !item._value)
            uninput.push(item._text);
        });
        this._canvas.remove(...uninput);
      }
      this.discardActiveObject();
      const image = await loadHTMLImage(this._canvas.getElement().toDataURL());
      const { left, top, width, height } = rules.bottomImage;
      context?.drawImage(image, 0, 0, image.width, image.height, left, top, width, height);
      this._canvas.add(...uninput);
    }
    return undefined;
  }

  render() {
    const { initLoading, loading, error } = this.state;
    const { className, bottomImage, watermarkURL, rules } = this.props;

    return (
      <div ref={this.refWrapper} className={className}>
        <div className={styles.mugContentCanvas}>
          {bottomImage &&
            <img
              className={styles.mugContentBottom}
              src={bottom7nCrop(bottomImage.image, rules.bottomImage, 'jpg')}
              onLoad={this.onBottomImageLoad}
            />
          }
          {watermarkURL &&
            <img className={styles.mugContentWatermark} src={watermarkURL} />
          }
          <canvas ref={this.refElement}  />
          {(initLoading || loading) && (
            <div className={styles.mugContentLoading}>
              <Loading />
            </div>
          )}
          {error && (
            <div className={styles.mugContentErrorWrap}>
              <div className={styles.mugContentError}>{i18n.format('modules.error.load.font')}</div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

Content.Validator = (rule: RuleLineText, lineTexts: LineTextOption[]): {
  status: ValidatorStatus,
  message: React.ReactNode[]
} => {
  let status = ValidatorStatus.pass;
  const message = [];
  for (let i = 0; i < lineTexts.length; i++) {
    const _value = lineTexts[i]._value || '';
    if (rule.isRequire && _value.length === 0) {
      if (status) status = ValidatorStatus.error;
      message.push(<span>{i18n.format('modules.mug.font.validator')} {lineTexts[i].value}"</span>);
    }
  }
  return {
    status,
    message
  };
}

export default Content;
